import isEmpty from 'lodash/isEmpty';
import { getGlobalLogger } from '@zg-rentals/logger-base';

const logger = getGlobalLogger('models/conversation');

interface ConversationMessage {
  isMessageOwner: boolean;
  [key: string]: unknown; // Allow for other properties in the conversation message
}

interface ConversationDetails {
  conversation?: Array<ConversationMessage>;
  conversationId?: string;
  hasUnreadMessage?: boolean;
  isMultifamily?: boolean;
  mostRecentMessageTimestampMs?: number;
  listingAlias?: string | null;
}

class Conversation {
  lastSender?: 'user' | 'landlord' | null;
  id?: string;
  lastMessageTimestamp?: number;
  isUnread?: boolean;
  satelliteEnabled?: boolean;

  private constructor(conversationDetails: ConversationDetails) {
    const {
      conversation = [],
      conversationId,
      hasUnreadMessage,
      isMultifamily,
      mostRecentMessageTimestampMs,
    } = conversationDetails;

    // Determine the last sender
    this.lastSender = conversation.slice(-1)[0].isMessageOwner === true ? 'user' : 'landlord';

    this.id = conversationId;
    this.lastMessageTimestamp = mostRecentMessageTimestampMs;
    this.isUnread = hasUnreadMessage;
    this.satelliteEnabled = !isMultifamily; // Disable for MF listings. They currently do not use Satellite.
  }

  /**
   * Creates a Conversation instance from API response data.
   *
   * Performs two-layer validation to protect against backend issues:
   * 1. If expectedAliasEncoded is provided, verifies that conversation.listingAlias matches it
   * 2. Returns null if validation fails, preventing corrupted data from being stored
   *
   * This defensive validation is critical for catching backend bugs where the API
   * might return conversations for the wrong listing (HPWEB-6574).
   *
   * @param conversationDetails - The conversation data object from the API response
   * @param expectedAliasEncoded - Optional. The listing alias we expect for this conversation.
   *                              If provided and doesn't match, returns null instead of creating a conversation.
   * @returns Conversation instance if data is valid, null if validation fails or data is incomplete
   *
   * @example
   * // With validation (recommended)
   * const conversation = Conversation.create(apiData, listing.aliasEncoded);
   *
   * @example
   * // Without validation (backward compatible)
   * const conversation = Conversation.create(apiData);
   */
  static create(conversationDetails: ConversationDetails, expectedAliasEncoded?: string): Conversation | null {
    const { conversation = [], listingAlias } = conversationDetails;

    // If a conversation exists between a landlord and tenant, the API should always return
    // a conversation array of size 1, featuring the most recent message in the conversation
    // chain.
    if (isEmpty(conversation)) {
      // If conversation is empty, return null as per existing logic
      return null;
    }

    // SECONDARY VALIDATION: Verify the conversation's listingAlias matches expected value
    // This provides defense-in-depth against backend issues or data corruption
    if (expectedAliasEncoded && listingAlias && listingAlias !== expectedAliasEncoded) {
      logger?.error(
        `Conversation model validation failed: Listing alias mismatch. ` +
          `Expected: ${expectedAliasEncoded}, Got: ${listingAlias}, ` +
          `ConversationId: ${conversationDetails.conversationId || 'unknown'}`,
        {
          expectedAlias: expectedAliasEncoded,
          receivedAlias: listingAlias,
          conversationId: conversationDetails.conversationId,
        },
      );
      return null;
    }

    // If not empty, create and return an instance of Conversation
    return new Conversation(conversationDetails);
  }
}

export default Conversation;
